/**
 * Phase 27.40 — Landing Page bilingual helper.
 *
 * Provides a compact EN/ES translation dictionary + a tiny `t(key, lang)`
 * helper for the public landing page. Only the form chrome and system
 * messages are translated — admin-authored headlines/intros stay in the
 * language the admin wrote them, which is expected and matches how KR
 * agents already localize copy per audience.
 *
 * The visitor's choice is persisted in localStorage under the
 * `kr-lp-language:{slug}` key so returning visitors don't see the picker
 * twice. When absent → picker is shown. When present → skip picker.
 */

export const LP_LANG_STORAGE_PREFIX = 'kr-lp-language:';

const STRINGS = {
  // ── language picker ─────────────────────────────────────────────
  picker_title: {
    en: 'Choose your preferred language',
    es: 'Elige tu idioma preferido',
  },
  picker_subtitle: {
    en: 'You can change this later using the button in the top-right corner.',
    es: 'Puedes cambiarlo después con el botón en la esquina superior derecha.',
  },
  picker_english: { en: 'English', es: 'English' },
  picker_spanish: { en: 'Español', es: 'Español' },
  picker_continue: {
    en: 'Continue',
    es: 'Continuar',
  },
  // ── top-right language switcher button ──────────────────────────
  lang_switch_label: { en: 'Language', es: 'Idioma' },
  // ── form section labels ─────────────────────────────────────────
  form_heading: { en: 'Get in Touch', es: 'Contáctame' },
  form_schedule_heading: { en: 'Schedule a call', es: 'Agendar una llamada' },
  form_first_name: { en: 'First Name', es: 'Nombre' },
  form_last_name: { en: 'Last Name', es: 'Apellido' },
  form_phone: { en: 'Phone Number', es: 'Teléfono' },
  form_email: { en: 'Email', es: 'Correo electrónico' },
  form_zip: { en: 'ZIP Code', es: 'Código Postal' },
  form_preferred_contact: {
    en: 'Preferred Contact Method',
    es: 'Método de Contacto Preferido',
  },
  contact_any: { en: 'Any', es: 'Cualquiera' },
  contact_phone: { en: 'Phone call', es: 'Llamada' },
  contact_sms: { en: 'Text message', es: 'Mensaje de texto' },
  contact_email: { en: 'Email', es: 'Correo electrónico' },
  form_message: { en: 'How can I help you? (Optional)', es: '¿Cómo puedo ayudarte? (Opcional)' },
  form_message_placeholder: {
    en: "Tell me briefly what you're looking for…",
    es: 'Cuéntame brevemente qué estás buscando…',
  },
  services_heading: { en: "I'm interested in", es: 'Me interesa' },
  submit_button: { en: 'Send my info', es: 'Enviar mi información' },
  submitting: { en: 'Sending…', es: 'Enviando…' },
  // ── consent checkboxes (kept short — full legal text stays as-is) ─
  consent_generic: {
    en: 'I agree to be contacted about the services I selected.',
    es: 'Acepto ser contactado sobre los servicios que seleccioné.',
  },
  // ── success message ─────────────────────────────────────────────
  thank_you: { en: 'Thank you', es: 'Gracias' },
  thank_you_body: {
    en: "Your message has been sent. I'll be in touch shortly.",
    es: 'Tu mensaje fue enviado. Te contactaré en breve.',
  },
  // ── appointment section ─────────────────────────────────────────
  appt_choose_date: { en: 'Choose a date', es: 'Elige una fecha' },
  appt_choose_slot: { en: 'Available times', es: 'Horarios disponibles' },
  appt_notes: { en: 'What would you like to discuss? (Optional)', es: '¿De qué te gustaría hablar? (Opcional)' },
  appt_book_button: { en: 'Book my appointment', es: 'Reservar mi cita' },
  // ── validation ─────────────────────────────────────────────────
  err_required: { en: 'Required', es: 'Requerido' },
  err_email: { en: 'Enter a valid email', es: 'Ingresa un correo válido' },
  err_phone: { en: 'Enter a valid phone number', es: 'Ingresa un teléfono válido' },
  // ── mode switcher ──────────────────────────────────────────────
  mode_info: { en: 'Get info', es: 'Obtener información' },
  mode_appt: { en: 'Schedule call', es: 'Agendar llamada' },
};

export function t(key, lang = 'en') {
  const entry = STRINGS[key];
  if (!entry) return key;
  return entry[lang] || entry.en || key;
}

export function getStoredLanguage(slug) {
  try {
    return localStorage.getItem(LP_LANG_STORAGE_PREFIX + slug);
  } catch (_) {
    return null;
  }
}

export function setStoredLanguage(slug, lang) {
  try {
    localStorage.setItem(LP_LANG_STORAGE_PREFIX + slug, lang);
  } catch (_) { /* ignore */ }
}

// Bilingual service-catalogue labels. Falls back to the original
// English label from `SERVICE_CATALOG` when we don't have a translation.
export const SERVICE_LABEL_ES = {
  medicare_advantage: 'Medicare Advantage',
  medicare_supplement: 'Suplemento de Medicare',
  final_expense: 'Seguro Funerario',
  term_life: 'Seguro de Vida a Término',
  whole_life: 'Seguro de Vida Entera',
  iul: 'IUL (Indexed Universal Life)',
  annuity: 'Anualidad',
  dental_vision: 'Dental y Visión',
  ancillary: 'Coberturas Complementarias',
  aca: 'Obamacare (ACA)',
  short_term_medical: 'Cobertura Médica Temporal',
  supplemental_health: 'Salud Suplementaria',
  hospital_indemnity: 'Indemnización Hospitalaria',
  accident: 'Seguro de Accidente',
  critical_illness: 'Enfermedad Crítica',
  cancer: 'Seguro de Cáncer',
  disability: 'Seguro de Discapacidad',
  long_term_care: 'Cuidado a Largo Plazo',
  auto: 'Auto',
  home: 'Hogar',
  travel: 'Viaje',
  pet: 'Mascotas',
  business: 'Negocio',
  employee_benefits: 'Beneficios de Empleados',
};

export function serviceLabel(catalogEntry, lang) {
  if (lang === 'es') {
    return SERVICE_LABEL_ES[catalogEntry.key] || catalogEntry.label;
  }
  return catalogEntry.label;
}
